import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ClubFeatureFlags {
  cta: boolean; predictor: boolean; survey: boolean;
  matchAnalytics: boolean; ordering: boolean; poll: boolean;
}

const DEFAULTS: ClubFeatureFlags = {
  cta: true, predictor: true, survey: true, matchAnalytics: true, ordering: false, poll: true,
};

interface FeatureFlagRow {
  cta_enabled: boolean | null;
  predictor_enabled: boolean | null;
  survey_enabled: boolean | null;
  match_analytics_enabled: boolean | null;
  ordering_enabled: boolean | null;
  poll_enabled: boolean | null;
  /** The tenant hid the venue kiosk's ordering block on its page (D2). */
  venue_ordering_hidden: boolean | null;
}

export function useClubFeatureFlags(clubId: string | null) {
  const { data, isLoading } = useQuery({
    queryKey: ["club-feature-flags", clubId],
    enabled: !!clubId,
    queryFn: async () => {
      // venue_ordering_hidden is newer than the generated types (regenerated
      // in the clean-up phase), hence the untyped client.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = (await (supabase as any)
        .from("club_settings")
        .select("cta_enabled, predictor_enabled, survey_enabled, match_analytics_enabled, ordering_enabled, poll_enabled, venue_ordering_hidden")
        .eq("club_id", clubId!)
        .maybeSingle()) as { data: FeatureFlagRow | null; error: { message: string } | null };
      if (error) throw error;
      return data;
    },
  });
  const flags: ClubFeatureFlags = data
    ? {
        cta: data.cta_enabled ?? true,
        predictor: data.predictor_enabled ?? true,
        survey: data.survey_enabled ?? true,
        matchAnalytics: data.match_analytics_enabled ?? true,
        ordering: data.ordering_enabled ?? false,
        poll: data.poll_enabled ?? true,
      }
    : DEFAULTS;
  // Only meaningful for a tenant served by the venue's kiosk: there the fan
  // page shows ordering unless this is set, whatever `ordering` says.
  const venueOrderingHidden = data?.venue_ordering_hidden ?? false;
  return { flags, venueOrderingHidden, isLoading: !!clubId && isLoading };
}
